import { AssetDoc } from '@livekatsomo/models';
import {
  UploadAssetFunction,
  UploadProgressFunction,
} from '@livekatsomo/types';
import { SelectAssetDialog } from '@livekatsomo/web/ui-components/assets';
import Button from '@mui/material/Button';
import { Editor, JSONContent } from '@tiptap/react';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { TipTapEditor } from '../TipTapEditor/TipTapEditor';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
// import { DevTool } from '@hookform/devtools';

export interface EditorFormProps {
  debug?: boolean;
  document?: JSONContent;
  assets: AssetDoc[];
  onSubmit: (document: JSONContent) => Promise<void>;
  onUploadAsset: UploadAssetFunction;
  onClose: () => void;
}

/**
 * EditorForm component for editing and publishing JSON content using the TipTap editor.
 * @param debug - Optional flag to enable debug mode.
 * @param document - Optional JSONContent object to pre-populate the editor with.
 * @param assets - Array of AssetDoc objects to display in the asset selection dialog.
 * @param onSubmit - Function to handle form submission. Expects a JSONContent object as input.
 * @param onUploadAsset - Function to handle asset uploads. Expects an UploadAssetFunction and an optional UploadProgressFunction as input.
 * @param onClose - Function to handle closing the form.
 */
export function TipTapEditorForm({
  document,
  assets,
  debug,
  onSubmit,
  onUploadAsset,
  onClose,
}: EditorFormProps) {
  const editorRef = useRef<Editor | null>(null);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: {
      document: document,
    },
  });

  const handlePublish = useCallback(
    async (data: { document: JSONContent | undefined }) => {
      if (!data.document) return;
      try {
        await onSubmit(data.document);
        methods.reset(methods.getValues());
      } catch (error) {
        if (error instanceof Error)
          methods.setError('document', { message: error.message });
      }
    },
    [onSubmit, methods],
  );

  const handleReset = () => {
    methods.reset();
    const document = methods.getValues().document;
    editorRef.current?.commands.setContent(document || '');
  };

  const handleSelectAsset = async (
    value: { asset: AssetDoc | File[]; alt?: string },
    setProgress?: UploadProgressFunction,
  ): Promise<void> => {
    if (!value || !editorRef.current) return;
    if (Array.isArray(value.asset)) {
      console.log('upload new asset', value);
      const { asset: files, alt } = value;
      const asset = await onUploadAsset(
        {
          alt,
          files,
        },
        setProgress,
      );
      if (asset) {
        editorRef.current?.commands.setImage({
          image: asset,
        });
      }
    } else {
      console.log('select existing asset', value);
      editorRef.current?.chain().focus().setImage({ image: value.asset }).run();
    }
  };

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = methods;
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handlePublish)} onReset={handleReset}>
        <DialogContent>
          <Controller
            render={({ field }) => (
              <TipTapEditor
                content={field.value}
                onAddImage={() => setAssetDialogOpen(true)}
                showHTML={debug}
                debug={debug}
                onUpdate={({ editor }) => {
                  field.onChange(editor.getJSON());
                }}
                ref={editorRef}
              />
            )}
            name="document"
            defaultValue=""
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button type="reset" disabled={isSubmitting || !isDirty}>
            {t('Reset')}
          </Button>
          <Button type="submit" disabled={!isDirty || isSubmitting || !isDirty}>
            {t('Save')}
          </Button>
        </DialogActions>
      </form>
      <SelectAssetDialog
        assets={assets}
        open={assetDialogOpen}
        onClose={() => setAssetDialogOpen(false)}
        onSubmit={handleSelectAsset}
      />
    </FormProvider>
  );
}
